import React, { useState, useRef, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/hooks';
import {
  useGetMeQuery,
  useUpdateMeMutation,
  useRequestEmailChangeMutation,
  useVerifyEmailChangeMutation,
  useRequestPhoneChangeMutation,
  useVerifyPhoneChangeMutation,
  useUploadAvatarMutation,
} from '@/redux/internal-services/usersApi';
import { getErrorDetail } from '@/utils/apiError';
import { useGetCountriesQuery } from '@/redux/internal-services/geoApi';
import { setUserName } from '@/redux/slices/sessionStateSlice';
import { Button } from '@/components/UI/Button';
import { DialCodeSelect } from '@/components/UI/GeoSelect';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';

const inputCls = fieldClass();
const readOnlyCls = cn(fieldClass(), 'cursor-not-allowed bg-ui-subtle text-ui-text2');
const otpInputCls = cn(fieldClass(), 'font-mono tracking-widest');

const ProfilePage: React.FC = () => {
  const fileRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const { data: me } = useGetMeQuery();
  const [updateMe, { isLoading: savingProfile }] = useUpdateMeMutation();
  const [uploadAvatar, { isLoading: uploading }] = useUploadAvatarMutation();
  const [requestEmailChange, { isLoading: requestingEmail }] = useRequestEmailChangeMutation();
  const [verifyEmailChange, { isLoading: verifyingEmail }] = useVerifyEmailChangeMutation();
  const [requestPhoneChange, { isLoading: requestingPhone }] = useRequestPhoneChangeMutation();
  const [verifyPhoneChange, { isLoading: verifyingPhone }] = useVerifyPhoneChangeMutation();
  const { data: countriesData } = useGetCountriesQuery();
  const roleNames = useAppSelector((state) => state.sessionState.roleNames);

  // ── Personal info ─────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [avatarError, setAvatarError] = useState('');

  useEffect(() => {
    if (hydrated || !me) return;
    setName(me.name ?? '');
    setAvatarUrl(me.profile_picture_url ?? '');
    setHydrated(true);
  }, [me, hydrated]);

  const profileDirty =
    hydrated && (name !== (me?.name ?? '') || avatarUrl !== (me?.profile_picture_url ?? ''));

  const handleSaveProfile = async () => {
    const trimmedName = name.trim();
    await updateMe({
      name: trimmedName || undefined,
      profile_picture_url: avatarUrl || undefined,
    })
      .unwrap()
      .catch(() => {});
    if (trimmedName) dispatch(setUserName(trimmedName));
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 4000);
  };

  const handleAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const url = await uploadAvatar(fd).unwrap();
      setAvatarUrl(url);
    } catch {
      setAvatarError('Failed to upload picture. Please try again.');
    }
  };

  // ── Email change ──────────────────────────────────────────────────────────
  const [emailStep, setEmailStep] = useState<'idle' | 'input' | 'verify'>('idle');
  const [newEmail, setNewEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [emailError, setEmailError] = useState('');

  const handleRequestEmail = async () => {
    setEmailError('');
    try {
      await requestEmailChange({ new_email: newEmail.trim() }).unwrap();
      setEmailStep('verify');
    } catch (e) {
      setEmailError(getErrorDetail(e) ??'Could not send the verification code. Please try again.');
    }
  };

  const handleVerifyEmail = async () => {
    setEmailError('');
    try {
      await verifyEmailChange({ otp: emailOtp.trim() }).unwrap();
      setEmailStep('idle');
      setNewEmail('');
      setEmailOtp('');
    } catch (e) {
      setEmailError(getErrorDetail(e) ??'Invalid code. Please check and try again.');
    }
  };

  // ── Phone change ──────────────────────────────────────────────────────────
  const [phoneStep, setPhoneStep] = useState<'idle' | 'input' | 'verify'>('idle');
  const [dialCode, setDialCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const splitPhone = (full: string, codes: string[]) => {
    const sorted = [...codes].sort((a, b) => b.length - a.length);
    const match = sorted.find((c) => full.startsWith(c));
    return match ? { dc: match, num: full.slice(match.length) } : { dc: '+1', num: full };
  };

  const phoneHydrated = useRef(false);
  useEffect(() => {
    if (phoneHydrated.current || !me?.phone || !countriesData?.data) return;
    const codes = countriesData.data.map((c: { dial_code: string }) => c.dial_code);
    const { dc, num } = splitPhone(me.phone, codes);
    setDialCode(dc);
    setPhoneNumber(num);
    phoneHydrated.current = true;
  }, [me, countriesData]);

  const combinedPhone = `${dialCode}${phoneNumber.trim()}`;

  const handleRequestPhone = async () => {
    setPhoneError('');
    try {
      await requestPhoneChange({ new_phone: combinedPhone }).unwrap();
      setPhoneStep('verify');
    } catch (e) {
      setPhoneError(getErrorDetail(e) ??'Could not send the verification code. Please check the number.');
    }
  };

  const handleVerifyPhone = async () => {
    setPhoneError('');
    try {
      await verifyPhoneChange({ otp: phoneOtp.trim() }).unwrap();
      setPhoneStep('idle');
      setPhoneOtp('');
    } catch (e) {
      setPhoneError(getErrorDetail(e) ??'Invalid code. Please check and try again.');
    }
  };


  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="px-8 pt-8 pb-6 shrink-0">
        <h1 className="text-h2 text-ui-text">My Profile</h1>
        <p className="text-sm text-ui-text2 mt-1">
          Keep your profile up to date so your name appears correctly on proposals and reports.
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-8 pb-8">
        <div className="flex flex-col lg:flex-row gap-8 max-w-4xl">

          <div className="flex-1 min-w-0 space-y-6">

            {/* ── Personal Info ── */}
            <div className="card-surface overflow-hidden">
              <div className="flex items-center justify-between gap-3 border-b border-ui-border px-5 py-4">
                <h2 className="text-h3 text-ui-text">Personal Information</h2>
                <Button
                  size="sm"
                  loading={savingProfile || uploading}
                  disabled={!profileDirty}
                  onClick={handleSaveProfile}
                >
                  Save
                </Button>
              </div>
              <div className="p-5 space-y-5">
                {/* Avatar */}
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-full bg-ui-subtle overflow-hidden flex items-center justify-center ring-1 ring-ui-border shrink-0">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="Profile" className="h-full w-full object-cover" />
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7 text-ui-text3">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="text-sm font-medium text-accent-text hover:underline disabled:opacity-50"
                    >
                      {uploading ? 'Uploading…' : avatarUrl ? 'Change picture' : 'Upload picture'}
                    </button>
                    <p className="text-xs text-ui-text3 mt-0.5">JPEG, PNG, WebP · up to 5 MB</p>
                    {avatarError && <p className="text-xs text-danger-text mt-1">{avatarError}</p>}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatar} />
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-ui-text mb-1.5">Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    className={inputCls}
                  />
                </div>

                {/* Role — read-only */}
                <div>
                  <label className="block text-sm font-medium text-ui-text mb-1.5">Role</label>
                  <div className="flex flex-wrap items-center gap-2">
                    {roleNames.length > 0 ? (
                      roleNames.map((role) => (
                        <span
                          key={role}
                          className="inline-flex items-center px-3 py-1.5 rounded-full bg-accent-soft text-accent-text text-xs font-medium"
                        >
                          {role}
                        </span>
                      ))
                    ) : (
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-accent-soft text-accent-text text-xs font-medium">
                        Member
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-ui-text3 mt-1.5">
                    Your role is assigned by an admin and determines what you can access.
                  </p>
                </div>

                {profileSaved && <p className="text-xs text-success-text">Profile saved.</p>}
              </div>
            </div>

            {/* ── Contact Information ── */}
            <div className="card-surface overflow-hidden">
              <div className="border-b border-ui-border px-5 py-4">
                <h2 className="text-h3 text-ui-text">Contact Information</h2>
              </div>
              <div className="p-5 space-y-5">

                {/* Email */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-medium text-ui-text">
                      Email Address
                      {me?.email_verified && (
                        <span className="ml-2 text-xs font-normal text-success-text">Verified</span>
                      )}
                    </label>
                    {emailStep === 'idle' && (
                      <button
                        type="button"
                        onClick={() => setEmailStep('input')}
                        className="text-xs text-accent-text font-medium hover:underline"
                      >
                        Change
                      </button>
                    )}
                  </div>

                  {emailStep === 'idle' && (
                    <input type="email" value={me?.email ?? ''} readOnly disabled className={readOnlyCls} />
                  )}

                  {emailStep === 'input' && (
                    <div className="space-y-3">
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="New email address"
                        className={inputCls}
                        autoFocus
                      />
                      {emailError && <p className="text-xs text-danger-text">{emailError}</p>}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setEmailStep('idle'); setNewEmail(''); setEmailError(''); }}
                        >
                          Cancel
                        </Button>
                        <Button size="sm" loading={requestingEmail} disabled={!newEmail.trim()} onClick={handleRequestEmail}>
                          Send Code
                        </Button>
                      </div>
                    </div>
                  )}

                  {emailStep === 'verify' && (
                    <div className="space-y-3">
                      <p className="text-xs text-ui-text2">
                        Enter the 6-digit code sent to <strong>{newEmail}</strong>.
                      </p>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={emailOtp}
                        onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ''))}
                        placeholder="000000"
                        className={otpInputCls}
                        autoFocus
                      />
                      {emailError && <p className="text-xs text-danger-text">{emailError}</p>}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setEmailStep('idle'); setEmailOtp(''); setEmailError(''); }}
                        >
                          Cancel
                        </Button>
                        <Button size="sm" loading={verifyingEmail} disabled={emailOtp.length !== 6} onClick={handleVerifyEmail}>
                          Verify
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-medium text-ui-text">
                      Phone Number
                      {me?.phone_verified && (
                        <span className="ml-2 text-xs font-normal text-success-text">Verified</span>
                      )}
                    </label>
                    {phoneStep === 'idle' && me?.phone?.trim() && (
                      <button
                        type="button"
                        onClick={() => setPhoneStep('input')}
                        className="text-xs text-accent-text font-medium hover:underline"
                      >
                        Change
                      </button>
                    )}
                  </div>

                  {phoneStep === 'idle' && (
                    me?.phone?.trim() ? (
                      <input
                        type="tel"
                        value={(() => {
                          if (!countriesData?.data) return me.phone;
                          const codes = countriesData.data.map((c: { dial_code: string }) => c.dial_code);
                          const { dc, num } = splitPhone(me.phone, codes);
                          return `${dc} ${num}`;
                        })()}
                        readOnly
                        disabled
                        className={readOnlyCls}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPhoneStep('input')}
                        className="flex h-11 w-full items-center gap-2 rounded-full border border-dashed border-ui-border3 px-4 text-left text-sm text-ui-text3 transition-colors hover:border-ui-ring hover:bg-ui-subtle hover:text-ui-text2"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4 shrink-0">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                        </svg>
                        Add phone number
                      </button>
                    )
                  )}

                  {phoneStep === 'input' && (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <DialCodeSelect value={dialCode} onChange={setDialCode} className="w-32 shrink-0" />
                        <input
                          type="tel"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          placeholder="Enter phone number"
                          className={inputCls}
                          autoFocus
                        />
                      </div>
                      {phoneError && <p className="text-xs text-danger-text">{phoneError}</p>}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setPhoneStep('idle'); setPhoneError(''); }}
                        >
                          Cancel
                        </Button>
                        <Button size="sm" loading={requestingPhone} disabled={!phoneNumber.trim()} onClick={handleRequestPhone}>
                          Send Code
                        </Button>
                      </div>
                    </div>
                  )}

                  {phoneStep === 'verify' && (
                    <div className="space-y-3">
                      <p className="text-xs text-ui-text2">
                        Enter the 6-digit code sent to <strong>{combinedPhone}</strong>.
                      </p>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={phoneOtp}
                        onChange={(e) => setPhoneOtp(e.target.value.replace(/\D/g, ''))}
                        placeholder="000000"
                        className={otpInputCls}
                        autoFocus
                      />
                      {phoneError && <p className="text-xs text-danger-text">{phoneError}</p>}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setPhoneStep('idle'); setPhoneOtp(''); setPhoneError(''); }}
                        >
                          Cancel
                        </Button>
                        <Button size="sm" loading={verifyingPhone} disabled={phoneOtp.length !== 6} onClick={handleVerifyPhone}>
                          Verify
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>

          {/* Right panel */}
          <aside className="w-full lg:w-64 shrink-0">
            <div className="card-surface p-5">
              <p className="text-sm font-semibold text-ui-text mb-1">Your Profile</p>
              <p className="text-xs text-ui-text2 mb-4 leading-relaxed">
                Keep your profile up to date so your team can reach you and so your name appears
                correctly on proposals, invoices, and reports.
              </p>
              <nav className="space-y-1">
                {['Profile picture', 'Full name', 'Email address', 'Phone number'].map((item, i) => (
                  <div key={item} className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-ui-text">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-fill text-on-accent text-xs font-semibold">
                      {i + 1}
                    </span>
                    {item}
                  </div>
                ))}
              </nav>
            </div>
          </aside>

        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
